/**
 * Deterministic, seeded Pac-Man-style maze generator.
 *
 * Produces a 29-row x 28-col grid of TileType ints:
 *   Wall=0, Path=1, Dot=2, PowerPellet=3, Empty=4, Spawn=5
 * (matching the values in src/data/maze-data.ts — intentionally not imported
 * so this module stays free of game-data coupling).
 *
 * Level 1 returns the known-good original layout verbatim. For level >= 2 the
 * top region (rows 1-8) and bottom region (rows 18-27) are regenerated from a
 * seed derived from the level number, while the fixed band (row 0, rows 9-17,
 * row 28) is always copied verbatim so hardcoded game constants — player spawn
 * at (21,13), the ghost house, and the tunnel rows — stay compatible.
 */

const ROWS = 29
const COLS = 28

/** The horizontal side-tunnel row (cols 0 <-> 27 wrap here, and only here). */
const TUNNEL_ROW = 13

// Tile constants (mirrors TileType in src/data/maze-data.ts).
const WALL = 0
const PATH = 1
const DOT = 2
const POWER = 3
// Empty (4) and Spawn (5) only appear inside the fixed band, never regenerated.

/** Rows that are regenerated for level >= 2. Everything else is fixed band. */
const TOP_REGION = { start: 1, end: 8 } // inclusive
const BOTTOM_REGION = { start: 18, end: 27 } // inclusive

/**
 * Dot budget for generated levels (>= 2). paintPellets dots every open regen-region
 * cell, which runs ~330-350 — far denser than the hand-authored level-1 layout
 * (236 dots). Cap generated levels here; the excess is trimmed evenly (and
 * symmetrically) back to plain floor. Level 1 returns the original verbatim and never
 * calls paintPellets, so it keeps its authored 236 dots and is exempt.
 */
const MAX_DOTS = 250

/**
 * The known-good original 29x28 layout. Level 1 returns this exactly; higher
 * levels keep the fixed band (row 0, 9-17, 28) from here and regenerate the
 * top/bottom regions.
 */
const ORIGINAL_BAND: number[][] = [
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], // 0
  [0,2,2,2,2,2,2,2,2,2,2,2,2,0,0,2,2,2,2,2,2,2,2,2,2,2,2,0], // 1
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0], // 2
  [0,3,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,3,0], // 3
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0], // 4
  [0,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,0], // 5
  [0,2,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,2,0], // 6
  [0,2,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,2,0], // 7
  [0,2,2,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,2,2,0], // 8
  [0,0,0,0,0,0,2,0,0,0,0,0,1,0,0,1,0,0,0,0,0,2,0,0,0,0,0,0], // 9
  [4,4,4,4,4,0,2,0,0,0,0,0,1,0,0,1,0,0,0,0,0,2,0,4,4,4,4,4], // 10
  [4,4,4,4,4,0,2,0,0,1,1,1,1,1,1,1,1,1,1,0,0,2,0,4,4,4,4,4], // 11
  [0,0,0,0,0,0,2,0,0,1,0,0,0,4,4,0,0,0,1,0,0,2,0,0,0,0,0,0], // 12
  [1,1,1,1,1,1,2,1,1,1,0,4,4,4,4,4,4,0,1,1,1,2,1,1,1,1,1,1], // 13
  [0,0,0,0,0,0,2,0,0,1,0,4,4,5,5,4,4,0,1,0,0,2,0,0,0,0,0,0], // 14
  [4,4,4,4,4,0,2,0,0,1,0,0,0,0,0,0,0,0,1,0,0,2,0,4,4,4,4,4], // 15
  [4,4,4,4,4,0,2,0,0,1,1,1,1,1,1,1,1,1,1,0,0,2,0,4,4,4,4,4], // 16
  [0,0,0,0,0,0,2,0,0,1,0,0,0,0,0,0,0,0,1,0,0,2,0,0,0,0,0,0], // 17
  [0,2,2,2,2,2,2,2,2,2,2,2,2,0,0,2,2,2,2,2,2,2,2,2,2,2,2,0], // 18
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0], // 19
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0], // 20
  [0,3,2,2,0,0,2,2,2,2,2,2,2,1,1,2,2,2,2,2,2,2,0,0,2,2,3,0], // 21
  [0,0,0,2,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,2,0,0,0], // 22
  [0,0,0,2,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,2,0,0,0], // 23
  [0,2,2,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,2,2,0], // 24
  [0,2,0,0,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,0,0,2,0], // 25
  [0,2,0,0,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,0,0,2,0], // 26
  [0,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,0], // 27
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], // 28
]

/** Mulberry32 — small, fast, deterministic PRNG seeded from a 32-bit int. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** True if (row,col) lies in a region we regenerate. */
function inRegenRegion(row: number): boolean {
  return (
    (row >= TOP_REGION.start && row <= TOP_REGION.end) ||
    (row >= BOTTOM_REGION.start && row <= BOTTOM_REGION.end)
  )
}

/** Deep clone of the original layout. */
function cloneOriginal(): number[][] {
  return ORIGINAL_BAND.map((r) => r.slice())
}

/** True if a tile value is walkable (Path / Dot / PowerPellet). */
function isOpen(v: number): boolean {
  return v === PATH || v === DOT || v === POWER
}

/**
 * Flood-fill from (sr,sc) over open cells (Path/Dot/PowerPellet). Returns a
 * boolean grid of reached cells. Treats columns as wrapping is NOT done here —
 * the tunnel rows live in the fixed band and connect through col 6 / col 21
 * spines, so 4-dir adjacency within the grid suffices.
 */
function floodReached(grid: number[][], sr: number, sc: number): boolean[][] {
  const reached: boolean[][] = Array.from({ length: ROWS }, () =>
    new Array(COLS).fill(false),
  )
  if (!isOpen(grid[sr][sc])) return reached
  const stack: Array<[number, number]> = [[sr, sc]]
  reached[sr][sc] = true
  while (stack.length) {
    const [r, c] = stack.pop()!
    const neighbors: Array<[number, number]> = [
      [r - 1, c],
      [r + 1, c],
      [r, c - 1],
      [r, c + 1],
    ]
    for (const [nr, nc] of neighbors) {
      if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue
      if (reached[nr][nc]) continue
      if (!isOpen(grid[nr][nc])) continue
      reached[nr][nc] = true
      stack.push([nr, nc])
    }
  }
  return reached
}

/** Count open cells reachable from (21,13) and total open cells. */
function connectivityStats(grid: number[][]): { reached: number; open: number } {
  const reached = floodReached(grid, 21, 13)
  let reachedCount = 0
  let openCount = 0
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (isOpen(grid[r][c])) {
        openCount++
        if (reached[r][c]) reachedCount++
      }
    }
  }
  return { reached: reachedCount, open: openCount }
}

/**
 * Columns 0 and 27 are border walls in the regen regions; cols 1..26 are
 * generated as cols 1..13 then mirrored (col c <-> col 27-c). We only decide
 * cols 1..13 and copy across.
 */
const HALF_COLS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]

/**
 * Cells (by [row,col] on the left half, cols 1..13) that must stay open so the
 * regenerated regions connect to the fixed band and the player/ghost spawns:
 *  - col 6 and col 21 (mirror of 6) are open spines top-to-bottom in the band;
 *    keeping col 6 open through the regen rows links them.
 *  - col 1 (and mirror col 26) is the outer dot-loop side rail.
 *  - (21,13)/(21,14) are pinned Path near the central tunnel.
 */
function pinnedOpenLeft(row: number, col: number): boolean {
  if (col === 1) return true // outer side rail
  if (col === 6) return true // connector spine to fixed band
  if (row === 21 && col === 13) return true // pinned central path
  // Ghost-house UPWARD exit: row 9 (fixed band) opens cols 12/15 above the house; keep
  // row 8 cols 12/15 open too so jellyfish always have a guaranteed upward egress, not one
  // that merely happens to survive via the row-13 side tunnel. col 12 mirrors to col 15.
  if (row === 8 && col === 12) return true
  // Row 18 and row 27 are the top/bottom perimeters of the regions — keep the
  // horizontal rail open so the side rails connect across the top/bottom.
  if (row === 18) return true
  if (row === 27) return true
  return false
}

/**
 * Generate the left half (cols 1..13) wall/open pattern for the regen rows,
 * then mirror. Returns a full grid that still needs connectivity repair and
 * pellet assignment. Open cells are marked PATH; walls are WALL.
 */
function carveRegions(grid: number[][], rng: () => number, level: number): void {
  // Open fraction of the free (non-pinned) cells. Higher levels stay a touch
  // sparser (harder). This is a target, not a guarantee — growth stops early if
  // no more cells can open without creating a 2x2 "field".
  const t = Math.min(Math.max((level - 2) / 8, 0), 1)
  const targetOpen = 0.6 - t * 0.14

  const regenRows: number[] = []
  for (let r = TOP_REGION.start; r <= TOP_REGION.end; r++) regenRows.push(r)
  for (let r = BOTTOM_REGION.start; r <= BOTTOM_REGION.end; r++) regenRows.push(r)

  // Start from walls everywhere in the regen rows except the pinned-open cells.
  const free: Array<[number, number]> = []
  for (const r of regenRows) {
    for (const c of HALF_COLS) {
      const open = pinnedOpenLeft(r, c)
      grid[r][c] = open ? PATH : WALL
      grid[r][27 - c] = open ? PATH : WALL
      if (!open) free.push([r, c])
    }
    grid[r][0] = WALL
    grid[r][27] = WALL
  }

  // Shuffle the free-cell list deterministically (Fisher-Yates with the seeded
  // rng) so growth order — and thus the maze — varies per level but is stable.
  for (let i = free.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    const tmp = free[i]
    free[i] = free[j]
    free[j] = tmp
  }

  // Grow single-lane corridors: repeatedly open a wall cell that already touches
  // an open cell, but ONLY if opening it (and its mirror) creates no 2x2 field.
  // This constructively guarantees width-1 lanes — a cell can never widen a
  // corridor into a block. Multiple passes let corridors extend across the list.
  const totalFree = free.length
  let openCount = 0
  let progressed = true
  while (progressed && openCount < totalFree * targetOpen) {
    progressed = false
    for (const [r, c] of free) {
      if (grid[r][c] !== WALL) continue
      if (openCount >= totalFree * targetOpen) break
      // Must touch an existing open cell so growth stays connected to a corridor.
      if (openNeighborCount(grid, r, c) < 1) continue
      grid[r][c] = PATH
      grid[r][27 - c] = PATH
      if (createsField(grid, r, c) || createsField(grid, r, 27 - c)) {
        grid[r][c] = WALL
        grid[r][27 - c] = WALL
        continue
      }
      openCount++
      progressed = true
    }
  }
}

/**
 * Repair connectivity: flood from (21,13); any open regen-region cell not
 * reached gets carved a straight connector toward col 6 (the spine) on its row,
 * opening intermediate walls. Returns true if fully connected afterward.
 *
 * Note: this carving does NOT field-check (unlike carveRegions), so it may
 * transiently create a 2x2 field. That is fine — thinFields removes any field
 * afterward and generateMaze's final hasOpenField() check rejects the seed if a
 * field somehow survives. With the constructive carve, stragglers are rare, so
 * this connector path seldom runs.
 */
function repairConnectivity(grid: number[][]): boolean {
  const reached = floodReached(grid, 21, 13)
  for (let r = 0; r < ROWS; r++) {
    if (!inRegenRegion(r)) continue
    for (let c = 1; c <= 13; c++) {
      if (!isOpen(grid[r][c]) || reached[r][c]) continue
      // Carve horizontally toward col 6 spine; if already past, carve toward
      // the nearest reached open cell on the row, else open a vertical link.
      const target = 6
      const lo = Math.min(c, target)
      const hi = Math.max(c, target)
      for (let cc = lo; cc <= hi; cc++) {
        if (!isOpen(grid[r][cc])) grid[r][cc] = PATH
        grid[r][27 - cc] = grid[r][cc] // keep symmetry
      }
      // Also open a vertical step toward the band so the row links in.
      // Connect upward for the bottom region, downward for the top region.
      // Only ever mutate a regen row — never the protected fixed band. (Today
      // this is also safe because the adjacent band cells are already open, but
      // the inRegenRegion guard makes that independent of the band layout.)
      if (r >= BOTTOM_REGION.start) {
        if (inRegenRegion(r - 1) && !isOpen(grid[r - 1][6])) {
          grid[r - 1][6] = PATH
          grid[r - 1][21] = PATH
        }
      } else {
        if (inRegenRegion(r + 1) && !isOpen(grid[r + 1][6])) {
          grid[r + 1][6] = PATH
          grid[r + 1][21] = PATH
        }
      }
    }
  }
  const stats = connectivityStats(grid)
  return stats.reached === stats.open
}

/**
 * Count an open cell's walkable neighbors. Columns wrap horizontally ONLY on
 * the tunnel row (col 0 <-> col 27 are connected there); every other row uses
 * plain in-grid adjacency. Gating the wrap to TUNNEL_ROW makes the dead-end
 * test independent of the (separately maintained) fact that cols 0/27 stay
 * walled in the regen rows. A cell with < 2 such neighbors is a dead end.
 */
function openNeighborCount(grid: number[][], r: number, c: number): number {
  let n = 0
  const wrap = r === TUNNEL_ROW
  const leftC = c - 1 < 0 ? (wrap ? COLS - 1 : -1) : c - 1
  const rightC = c + 1 >= COLS ? (wrap ? 0 : -1) : c + 1
  const cells: Array<[number, number]> = [
    [r - 1, c],
    [r + 1, c],
    [r, leftC],
    [r, rightC],
  ]
  for (const [nr, nc] of cells) {
    if (nr < 0 || nr >= ROWS || nc < 0) continue
    if (isOpen(grid[nr][nc])) n++
  }
  return n
}

/**
 * Eliminate dead ends in the regen regions. A dead end is an open cell with
 * only one open neighbor — Pac-Man-style mazes are loop-based and have none.
 * For each dead end we open a second exit (a walled neighbor inside the regen
 * rows), preferring an opening whose cell-beyond is already open so the new
 * exit closes a loop rather than extending a fresh stub. Only ever OPENS cells
 * (never walls), so connectivity is preserved. Mirrored to keep L-R symmetry.
 * Iterates because opening can momentarily create a new one-exit cell; the
 * generateMaze loop rejects any seed that still has a dead end after this.
 */
function eliminateDeadEnds(grid: number[][]): void {
  // A walled neighbor is openable only if it sits inside a regen row and in a
  // non-border column (1..26) — never touch the fixed band or the outer wall.
  // Consequence: a dead-end regen cell whose only possible second exit would be
  // a fixed-band cell (e.g. a row-8 cell that could only link down into row 9)
  // is left unfixed here ON PURPOSE — we never carve the protected band. Such a
  // seed then fails the hasDeadEnd() check in generateMaze and is rejected in
  // favor of the next seed, so the shipped maze is still dead-end-free.
  const openable = (r: number, c: number): boolean =>
    inRegenRegion(r) && c >= 1 && c <= 26 && grid[r][c] === WALL

  const MAX_PASSES = 16
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    let changed = false
    // Scan the left half + center; mirror every edit so symmetry holds.
    for (let r = 0; r < ROWS; r++) {
      if (!inRegenRegion(r)) continue
      for (let c = 1; c <= 13; c++) {
        if (!isOpen(grid[r][c])) continue
        if (openNeighborCount(grid, r, c) >= 2) continue
        // Candidate directions to open, in fixed order [up,down,left,right].
        const dirs: Array<[number, number]> = [
          [-1, 0],
          [1, 0],
          [0, -1],
          [0, 1],
        ]
        // Candidates that can be opened WITHOUT creating a 2x2 field — opening
        // a 2nd exit must never widen the corridor into a block.
        const cands = dirs
          .map(([dr, dc]) => [r + dr, c + dc] as [number, number])
          .filter(([nr, nc]) => openable(nr, nc) && !wouldOpenField(grid, nr, nc))
        if (!cands.length) {
          // No field-safe way to give this dead end a 2nd exit. Erode it instead:
          // wall the leaf cell (and its mirror). Removing a degree-<=1 cell never
          // disconnects the rest and never creates a field; the erosion may shift
          // the dead end to the parent, which a later pass braids or erodes too.
          // Never erode a pinned-open cell (spawn/spine/connectors/rails).
          if (!pinnedOpenLeft(r, c)) {
            grid[r][c] = WALL
            grid[r][27 - c] = WALL
            changed = true
          }
          continue
        }
        // Prefer an opening that closes a loop: the cell two steps out (same
        // direction) is already open, so the new neighbor gets two exits too.
        // Tie-break by (row, col) so the choice is fully deterministic and does
        // not depend on Array.prototype.sort being stable for equal keys.
        const loopFirst = cands.sort((a, b) => {
          const beyondOpen = ([nr, nc]: [number, number]): number => {
            const br = nr + (nr - r)
            const bc = nc + (nc - c)
            if (br < 0 || br >= ROWS) return 0
            return isOpen(grid[br][(bc + COLS) % COLS]) ? 1 : 0
          }
          const d = beyondOpen(b) - beyondOpen(a)
          if (d !== 0) return d
          return a[0] - b[0] || a[1] - b[1]
        })
        const [nr, nc] = loopFirst[0]
        grid[nr][nc] = PATH
        grid[nr][27 - nc] = PATH // mirror
        changed = true
      }
    }
    if (!changed) break
  }
}

/**
 * True if any cell REACHABLE from the player spawn (21,13) is a dead end (< 2
 * open neighbors). Reachability-scoped on purpose: unreachable open cells are a
 * connectivity defect that generateMaze catches separately via connectivityStats
 * — here we only care about dead ends a player could actually walk into.
 */
function hasDeadEnd(grid: number[][]): boolean {
  const reached = floodReached(grid, 21, 13)
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (!isOpen(grid[r][c]) || !reached[r][c]) continue
      if (openNeighborCount(grid, r, c) < 2) return true
    }
  }
  return false
}

/** True if a 2x2 block of open cells starts at (r,c) — an open "field". */
function isOpenField(grid: number[][], r: number, c: number): boolean {
  return (
    isOpen(grid[r][c]) &&
    isOpen(grid[r][c + 1]) &&
    isOpen(grid[r + 1][c]) &&
    isOpen(grid[r + 1][c + 1])
  )
}

/**
 * True if cell (r,c) is part of any 2x2 open field — i.e. one of the (up to)
 * four 2x2 blocks that include (r,c) is fully open. Used to reject an opening
 * that would widen a corridor into a block.
 */
function createsField(grid: number[][], r: number, c: number): boolean {
  for (const [r0, c0] of [
    [r - 1, c - 1],
    [r - 1, c],
    [r, c - 1],
    [r, c],
  ] as Array<[number, number]>) {
    if (r0 < 0 || c0 < 0 || r0 + 1 >= ROWS || c0 + 1 >= COLS) continue
    if (isOpenField(grid, r0, c0)) return true
  }
  return false
}

/**
 * Non-mutating check: would opening (r,c) AND its mirror (27-c) create any 2x2
 * field? Tentatively opens both, tests, and restores the original values.
 */
function wouldOpenField(grid: number[][], r: number, c: number): boolean {
  const savedL = grid[r][c]
  const savedR = grid[r][27 - c]
  grid[r][c] = PATH
  grid[r][27 - c] = PATH
  const bad = createsField(grid, r, c) || createsField(grid, r, 27 - c)
  grid[r][c] = savedL
  grid[r][27 - c] = savedR
  return bad
}

/** True if any 2x2 open field exists anywhere in the grid. */
function hasOpenField(grid: number[][]): boolean {
  for (let r = 0; r < ROWS - 1; r++) {
    for (let c = 0; c < COLS - 1; c++) {
      if (isOpenField(grid, r, c)) return true
    }
  }
  return false
}

/** True if every open cell is reachable from the player spawn (21,13). */
function isFullyConnected(grid: number[][]): boolean {
  const stats = connectivityStats(grid)
  return stats.reached === stats.open
}

/**
 * Thin open "fields" down to single-lane corridors. Pac-Man mazes are made of
 * 1-wide lanes — never 2x2 (or larger) open blocks. eliminateDeadEnds opens
 * cells to kill dead ends, which can leave such fields; this pass walls a
 * redundant corner of each 2x2 open block, but ONLY when doing so keeps the
 * maze dead-end-free AND fully connected (tentative wall + validate + revert).
 * Only ever walls regen-region, non-pinned cells; mirrored to keep symmetry.
 * Residual fields that have no safe corner cause the seed to be rejected in
 * generateMaze, so accepted mazes are guaranteed field-free single-lane mazes.
 */
function thinFields(grid: number[][]): void {
  const MAX_PASSES = 24
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    let changed = false
    for (let r = 0; r < ROWS - 1; r++) {
      if (!inRegenRegion(r)) continue
      // Scan the left half AND the center seam (c=13 covers the cols 13-14
      // block that straddles the mirror axis) so no field is left for the
      // final hasOpenField() rejection. Mirrored walls keep symmetry.
      for (let c = 1; c <= 13; c++) {
        if (!isOpenField(grid, r, c)) continue
        const corners: Array<[number, number]> = [
          [r, c],
          [r, c + 1],
          [r + 1, c],
          [r + 1, c + 1],
        ]
        for (const [yr, yc] of corners) {
          if (yc > 13 || pinnedOpenLeft(yr, yc) || !inRegenRegion(yr)) continue
          if (grid[yr][yc] !== PATH && grid[yr][yc] !== DOT && grid[yr][yc] !== POWER) continue
          const savedL = grid[yr][yc]
          const savedR = grid[yr][27 - yc]
          grid[yr][yc] = WALL
          grid[yr][27 - yc] = WALL
          if (!hasDeadEnd(grid) && isFullyConnected(grid)) {
            changed = true
            break
          }
          grid[yr][yc] = savedL
          grid[yr][27 - yc] = savedR
        }
      }
    }
    if (!changed) break
  }
}

/**
 * After walls + connectivity are settled, paint pellets. Every reachable open
 * cell in the regen regions becomes a Dot, then four PowerPellets are placed
 * near the region corners (mirroring the original's 4-corner style).
 */
function paintPellets(grid: number[][]): void {
  const reached = floodReached(grid, 21, 13)
  for (let r = 0; r < ROWS; r++) {
    if (!inRegenRegion(r)) continue
    for (let c = 0; c < COLS; c++) {
      if (isOpen(grid[r][c]) && reached[r][c]) {
        grid[r][c] = DOT
      }
    }
  }
  // Power pellets near the four region corners. Original puts them at (3,1),
  // (3,26), (21,1)-ish and (21,26). Use the nearest reachable open cell.
  const corners: Array<[number, number]> = [
    [3, 1],
    [3, 26],
    [21, 1],
    [21, 26],
  ]
  for (const [pr, pc] of corners) {
    const spot = nearestReachableOpen(grid, reached, pr, pc)
    if (spot) grid[spot[0]][spot[1]] = POWER
  }
}

/**
 * Trim a level's dots down to {@link MAX_DOTS}. Excess dots are reverted to plain Path —
 * still walkable, just no pellet — so maze topology, L-R symmetry, and connectivity are
 * untouched; only the collectible count drops. Removals are taken in mirrored pairs (a
 * left-half cell and its col 27-c partner) and sampled at evenly offset indices so the
 * thinning is spread across the regen rows rather than clustered. Power pellets are POWER
 * (not DOT) so they are never touched. Invoked as an explicit stage of {@link generateMaze}
 * for level >= 2; level 1 returns the authored layout and never reaches here. A no-op when
 * the grid is already within budget. Exported for isolation testing (MG-11).
 */
export function capDots(grid: number[][]): void {
  let total = 0
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) if (grid[r][c] === DOT) total++
  }
  if (total <= MAX_DOTS) return

  // Removable dots live on the left half (cols 1..13); each pairs with its mirror at
  // 27-c, so removing both keeps the symmetry the generator guarantees. Only the
  // regen regions are eligible — the hand-authored fixed band is left intact.
  const leftDots: Array<[number, number]> = []
  for (let r = 0; r < ROWS; r++) {
    if (!inRegenRegion(r)) continue
    for (const c of HALF_COLS) {
      if (grid[r][c] === DOT && grid[r][27 - c] === DOT) leftDots.push([r, c])
    }
  }

  // Each pair removes 2 dots; round up so the result never lands above the budget.
  const pairs = Math.min(Math.ceil((total - MAX_DOTS) / 2), leftDots.length)
  // pairs <= leftDots.length, so the sample step (leftDots.length / pairs) is >= 1: every
  // floored index is strictly increasing and < leftDots.length — no duplicate sample
  // (which would revert the same cell twice and under-trim) and no out-of-range read.
  for (let i = 0; i < pairs; i++) {
    const [r, c] = leftDots[Math.floor(((i + 0.5) / pairs) * leftDots.length)]
    grid[r][c] = PATH
    grid[r][27 - c] = PATH
  }

  // Defensive: if the regen half lacked enough mirrored pairs to reach the budget (cannot
  // happen at today's ~330-350 raw density, but a future density / MAX_DOTS change could),
  // surface it in DEV rather than silently shipping an over-budget level.
  if (total - pairs * 2 > MAX_DOTS && import.meta.env?.DEV) {
    console.warn(
      `[maze-gen] capDots: only ${leftDots.length} removable pairs; level still has ` +
        `${total - pairs * 2} dots (> ${MAX_DOTS}).`,
    )
  }
}

/** Find the reachable open cell closest (Manhattan) to (pr,pc) in regen rows. */
function nearestReachableOpen(
  grid: number[][],
  reached: boolean[][],
  pr: number,
  pc: number,
): [number, number] | null {
  let best: [number, number] | null = null
  let bestDist = Infinity
  for (let r = 0; r < ROWS; r++) {
    if (!inRegenRegion(r)) continue
    for (let c = 0; c < COLS; c++) {
      if (!reached[r][c]) continue
      const v = grid[r][c]
      if (v !== DOT && v !== PATH) continue
      const dist = Math.abs(r - pr) + Math.abs(c - pc)
      if (dist < bestDist) {
        bestDist = dist
        best = [r, c]
      }
    }
  }
  return best
}

/**
 * Generate a maze for the given level.
 *
 * Level 1 returns the original layout verbatim. For level >= 2, rows 1-8 and
 * 18-27 are regenerated deterministically from a seed derived from the level,
 * with the fixed band copied verbatim. Output is always 29x28, L-R symmetric in
 * the regen rows, fully connected from the player spawn (21,13), and pelleted.
 */
export function generateMaze(level: number): number[][] {
  if (level <= 1) return cloneOriginal()

  // Try seeds derived from the level until we land a layout that satisfies every
  // invariant (connected, no dead ends, single-lane). Cap attempts, then fall
  // back to the original for safety.
  const MAX_TRIES = 120
  for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
    const grid = cloneOriginal()
    const seed = (level * 2654435761 + attempt * 40503) >>> 0
    const rng = mulberry32(seed)

    carveRegions(grid, rng, level)

    if (!repairConnectivity(grid)) {
      // Connector carving failed to fully link this seed; try the next.
      continue
    }

    // Alternate dead-end removal and field thinning to a fixpoint. eliminate
    // opens a 2nd exit per one-exit cell (never creating a field); thin walls a
    // redundant corner of each 2x2 field (never creating a dead end / breaking
    // connectivity). Iterating lets each pass unblock the other so more seeds
    // converge to a connected, single-lane, dead-end-free maze.
    for (let round = 0; round < 4; round++) {
      eliminateDeadEnds(grid)
      thinFields(grid)
      if (!hasDeadEnd(grid) && !hasOpenField(grid)) break
    }

    paintPellets(grid)
    capDots(grid) // trim to the MAX_DOTS budget (level 1 returns early and never reaches here)

    // Final validation: full connectivity, walkable spawn cells, NO dead ends,
    // and NO open fields (single-lane corridors only). Else try the next seed.
    const stats = connectivityStats(grid)
    if (stats.reached !== stats.open) continue
    if (!isOpen(grid[21][13]) || !isOpen(grid[21][14])) continue
    if (hasDeadEnd(grid)) continue
    if (hasOpenField(grid)) continue

    return grid
  }

  // Safety fallback: if no seed in MAX_TRIES produced a clean layout, return the
  // ORIGINAL band. This is itself dead-end-free — it is exactly what level 1
  // returns, and the MG-8 unit test proves the original has zero dead ends under
  // the horizontal-wrap rule. So the no-dead-end contract still holds on the
  // fallback path (the only cost is that this level reuses the level-1 layout).
  // Warn in DEV so this silent degradation (level N == level 1) is observable —
  // it means generation failed for every seed and the difficulty ramp is lost.
  if (import.meta.env?.DEV) {
    console.warn(
      `[maze-gen] level ${level}: all ${MAX_TRIES} seeds failed connectivity/dead-end checks; ` +
        `falling back to the level-1 layout. Difficulty ramp lost for this level.`,
    )
  }
  return cloneOriginal()
}
